import { useEffect, useRef, useState } from 'react';

const DEFAULT_DELAY = 3000;

export type UseMouseOverWithDelayedExitProps = {
	delay?: number;
	initiallyHovered?: boolean;
	// if this value changes, the component will reset the mouse over state
	resetKey?: string;
	// if true, the component will always be considered as mouse over
	isPinned?: boolean;
	onMouseOverChange?: (isMouseOver: boolean) => void;
};

export const useMouseOverWithDelayedExit = ({
	delay = DEFAULT_DELAY,
	initiallyHovered = false,
	resetKey,
	isPinned = false,
	onMouseOverChange,
}: UseMouseOverWithDelayedExitProps = {}) => {
	const [isMouseOver, setIsMouseOver] = useState(initiallyHovered);
	const hasInteractedRef = useRef(false);
	const timerRef = useRef<number | null>(null);

	const handleMouseEnter = (): void => {
		if (isPinned) {
			return;
		}
		if (!hasInteractedRef.current) {
			hasInteractedRef.current = true;
		}
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
		setIsMouseOver(true);
		onMouseOverChange?.(true);
	};

	const handleMouseLeave = (): void => {
		if (isPinned) {
			return;
		}
		if (!hasInteractedRef.current) {
			return;
		}
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
		}
		timerRef.current = window.setTimeout(() => {
			setIsMouseOver(false);
			onMouseOverChange?.(false);
		}, delay);
	};

	useEffect(() => {
		if (isPinned) {
			return;
		}
		hasInteractedRef.current = false;
		setIsMouseOver(initiallyHovered);
		onMouseOverChange?.(initiallyHovered);
		if (timerRef.current) {
			window.clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, [resetKey, initiallyHovered, isPinned, onMouseOverChange]);

	useEffect(() => {
		return () => {
			if (timerRef.current) {
				window.clearTimeout(timerRef.current);
			}
		};
	}, []);

	return { isMouseOver: isPinned || isMouseOver, handleMouseEnter, handleMouseLeave };
};
